import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import BrunoMessage from '../components/BrunoMessage';
import { useOrcamento } from '../context/OrcamentoContext';
import { TIPOS_CONSTRUCAO, PADROES_CONSTRUCAO } from '../data/etapasObra';
import estadosBrasil from '../data/estadosBrasil';
import LogService, { LOG_ACTIONS } from '../services/LogService';

function OrcamentoUpload() {
  const navigate = useNavigate();
  const { setConfig, file: ctxFile, filePreview: ctxPreview, tipo: ctxTipo, padrao: ctxPadrao, estado: ctxEstado } = useOrcamento();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(ctxFile);
  const [preview, setPreview] = useState(ctxPreview);
  const [tipo, setTipo] = useState(ctxTipo || 'casa_terrea');
  const [padrao, setPadrao] = useState(ctxPadrao || 'normal');
  const [estado, setEstado] = useState(ctxEstado || 'SP');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
  const maxSize = 10 * 1024 * 1024;

  const handleFile = (f) => {
    setError('');
    if (!allowedTypes.includes(f.type)) {
      setError('Formato invalido. Use PDF, JPG ou PNG.');
      return;
    }
    if (f.size > maxSize) {
      setError('Arquivo muito grande. Maximo 10MB.');
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = () => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setConfig({
        file: { base64, name: file.name, type: file.type, size: file.size },
        filePreview: preview,
        tipo,
        padrao,
        estado,
      });
      LogService.log(LOG_ACTIONS.ORCAMENTO_START, {
        details: { tipo, padrao, estado, fileName: file.name },
      });
      navigate('/orcamento/analise');
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-navy mb-2 font-heading">Orcamento por Planta</h1>
          <p className="text-gray-500">Envie a planta da obra e receba um orcamento estimado por etapas usando CUB</p>
        </div>

        {/* Bruno message */}
        <div className="mb-6">
          <BrunoMessage
            pose="acenando"
            message="Envie sua planta! Pode ser PDF, JPG ou PNG. Vou identificar os comodos e calcular o orcamento pra voce."
            type="tip"
          />
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
          {/* Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Planta da Obra</label>
            {!file ? (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                  dragOver ? 'border-brand-blue bg-blue-50' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
                }`}
              >
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm font-medium text-gray-600 mb-1">Arraste a planta aqui ou clique para selecionar</p>
                <p className="text-xs text-gray-400">PDF, JPG ou PNG (max 10MB)</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
                />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  {preview ? (
                    <img src={preview} alt="Preview" className="w-20 h-20 object-cover rounded-lg" />
                  ) : (
                    <div className="w-20 h-20 bg-red-50 rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                    <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button
                    onClick={removeFile}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
          </div>

          {/* Config selects */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de Construcao</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputClass}>
                {TIPOS_CONSTRUCAO.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Padrao</label>
              <select value={padrao} onChange={(e) => setPadrao(e.target.value)} className={inputClass}>
                {PADROES_CONSTRUCAO.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
              <select value={estado} onChange={(e) => setEstado(e.target.value)} className={inputClass}>
                {estadosBrasil.map((e) => (
                  <option key={e.sigla} value={e.sigla}>{e.nome} ({e.sigla})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!file}
            className="w-full py-4 text-base font-bold text-white bg-brand-orange rounded-xl hover:bg-brand-orange-dark cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-brand-orange/20"
          >
            Analisar Planta
          </button>
        </div>
      </main>
    </div>
  );
}

export default OrcamentoUpload;
